// src/components/TemplatePicker.tsx
// Template Picker — admin selecciona el template del sitio público de su org.
// Design system: Orion glass-morphism (o-card, o-btn-ghost, --acc1/2/3).

import React, { useEffect, useState, useCallback } from 'react';
import {
  listTemplates, getActiveTemplate, assignTemplate, saveTemplateOverrides,
  type SiteTemplate, type OrgTemplateConfig, type SiteTemplateTheme,
} from '@/lib/api';
import {
  Loader, Check, Palette, Eye, ChevronLeft, X, Type, Save,
  Layout as LayoutIcon, RotateCcw, Sparkles,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   UI Primitives
   ═══════════════════════════════════════════════════════════════ */

function Badge({ children, variant = 'default' }: {
  children: React.ReactNode;
  variant?: 'success' | 'accent' | 'default';
}) {
  const s: Record<string, string> = {
    success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
    accent:  'bg-[rgba(var(--acc2-rgb),.15)] text-[var(--acc2)] border-[rgba(var(--acc2-rgb),.25)]',
    default: 'bg-white/10 text-white/50 border-white/10',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${s[variant]}`}>{children}</span>;
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className="text-center py-14 text-white/20"><div className="mx-auto mb-3 opacity-40">{icon}</div><p>{text}</p></div>;
}

/* ═══════════════════════════════════════════════════════════════
   Constants
   ═══════════════════════════════════════════════════════════════ */

const COMPONENT_LABELS: Record<string, string> = {
  hero: 'Hero', navbar: 'Navbar', tripCard: 'Trip Card',
  blogCard: 'Blog Card', footer: 'Footer',
};

const VARIANT_LABELS: Record<string, string> = {
  'fullscreen-video': 'Video Fullscreen', 'split-image': 'Imagen Split',
  'parallax': 'Parallax', 'slider': 'Slider', 'minimal': 'Minimal',
  'sticky-transparent': 'Sticky Transparente', 'solid': 'Sólido',
  'hamburger-only': 'Solo Hamburguesa',
  'overlay': 'Overlay', 'side-by-side': 'Side by Side',
  'magazine': 'Magazine', 'classic': 'Clásico', 'horizontal': 'Horizontal',
  'full': 'Completo', 'split': 'Dividido',
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export function TemplatePicker({ addNotification }: { addNotification?: (n: any) => void }) {
  const [templates, setTemplates] = useState<SiteTemplate[]>([]);
  const [activeConfig, setActiveConfig] = useState<OrgTemplateConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Preview / detail
  const [selected, setSelected] = useState<SiteTemplate | null>(null);

  // Overrides editor
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrideColors, setOverrideColors] = useState<Record<string, string>>({});
  const [overrideFonts, setOverrideFonts] = useState<{ heading: string; body: string }>({ heading: '', body: '' });
  const [savingOverrides, setSavingOverrides] = useState(false);

  const [assigning, setAssigning] = useState(false);
  // true while we are waiting for auth:token-ready to retry the initial load
  const [retryOnAuth, setRetryOnAuth] = useState(false);

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    if (addNotification) addNotification({ type, message });
  }, [addNotification]);

  const load = useCallback(async () => {
    setLoading(true);
    setRetryOnAuth(false);
    try {
      const [tplRes, activeRes] = await Promise.all([
        listTemplates(),
        getActiveTemplate().catch(() => null),
      ]);
      setTemplates(tplRes.templates || []);
      setActiveConfig(activeRes);
      if (activeRes?.custom_overrides) {
        setOverrideColors(activeRes.custom_overrides.colors || {});
        setOverrideFonts({
          heading: activeRes.custom_overrides.fonts?.heading || '',
          body: activeRes.custom_overrides.fonts?.body || '',
        });
      }
    } catch (e: any) {
      if ((e as any)?.code === 'NO_TOKEN') {
        // Token not yet in localStorage (auth race). Wait silently for it.
        setRetryOnAuth(true);
      } else {
        notify('error', e.message);
      }
    }
    finally { setLoading(false); }
  }, [notify]);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Retry when LoginForm dispatches auth:token-ready (race condition fix)
  useEffect(() => {
    if (!retryOnAuth) return;
    const handler = () => load();
    window.addEventListener('auth:token-ready', handler);
    return () => window.removeEventListener('auth:token-ready', handler);
  }, [retryOnAuth, load]);

  const activeTemplateId = activeConfig?.template?.id;

  const handleAssign = async (tpl: SiteTemplate) => {
    if (tpl.id === activeTemplateId) return;
    setAssigning(true);
    try {
      await assignTemplate(tpl.id);
      notify('success', `Template "${tpl.name}" aplicado`);
      await load();
      setSelected(null);
    } catch (e: any) { notify('error', e.message); }
    finally { setAssigning(false); }
  };

  const handleSaveOverrides = async () => {
    setSavingOverrides(true);
    try {
      const colors: Record<string, string> = {};
      for (const [k, v] of Object.entries(overrideColors)) {
        if (v.trim()) colors[k] = v.trim();
      }
      const fonts: Record<string, string> = {};
      if (overrideFonts.heading.trim()) fonts.heading = overrideFonts.heading.trim();
      if (overrideFonts.body.trim()) fonts.body = overrideFonts.body.trim();
      await saveTemplateOverrides({
        colors: Object.keys(colors).length ? colors : undefined,
        fonts: Object.keys(fonts).length ? fonts as any : undefined,
      });
      notify('success', 'Overrides guardados');
      await load();
    } catch (e: any) { notify('error', e.message); }
    finally { setSavingOverrides(false); }
  };

  const handleResetOverrides = async () => {
    if (!confirm('¿Resetear todos los overrides de color/fuente?')) return;
    setSavingOverrides(true);
    try {
      await saveTemplateOverrides({});
      setOverrideColors({});
      setOverrideFonts({ heading: '', body: '' });
      notify('success', 'Overrides reseteados');
      await load();
    } catch (e: any) { notify('error', e.message); }
    finally { setSavingOverrides(false); }
  };

  // Detail / preview screen
  if (selected) {
    return (
      <TemplateDetail
        template={selected}
        isActive={selected.id === activeTemplateId}
        assigning={assigning}
        onAssign={() => handleAssign(selected)}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Palette size={20} style={{ color: 'var(--acc2)' }} />
            Apariencia
          </h1>
          <p className="text-xs text-white/30 mt-1">Elige el template y personaliza los colores de tu sitio público</p>
        </div>
        {activeConfig?.template && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/30">Template activo:</span>
            <Badge variant="success">{activeConfig.template.name}</Badge>
          </div>
        )}
      </div>

      {/* Active template overrides */}
      {activeConfig?.template && (
        <div className="o-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} style={{ color: 'var(--acc1)' }} />
              <span className="text-sm font-semibold text-white">Personalización</span>
              <span className="text-[10px] text-white/20">— overrides encima de "{activeConfig.template.name}"</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowOverrides(!showOverrides)}
                className="text-[10px] text-white/30 hover:text-white/50 transition-colors">
                {showOverrides ? 'Ocultar' : 'Editar colores y fuentes'}
              </button>
            </div>
          </div>

          {showOverrides && (
            <div className="space-y-4 border-t border-white/[.06] pt-4">
              {/* Color overrides */}
              <div>
                <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Colores</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                  {['primary', 'secondary', 'accent', 'background', 'surface', 'text'].map(key => {
                    const baseColor = activeConfig.template.theme.colors[key] || '#ffffff';
                    const overrideVal = overrideColors[key] || '';
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="relative">
                          <input
                            type="color"
                            value={overrideVal || baseColor}
                            onChange={e => setOverrideColors(p => ({ ...p, [key]: e.target.value }))}
                            className="w-8 h-8 rounded-lg border border-white/10 bg-transparent cursor-pointer appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-white/50 capitalize">{key}</p>
                          <p className="text-[10px] text-white/20 font-mono truncate">
                            {overrideVal ? overrideVal : `base: ${baseColor}`}
                          </p>
                        </div>
                        {overrideVal && (
                          <button onClick={() => setOverrideColors(p => {
                            const next = { ...p };
                            delete next[key];
                            return next;
                          })} className="p-0.5 text-white/20 hover:text-red-400 transition-colors" title="Resetear">
                            <X size={10} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Font overrides */}
              <div>
                <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold flex items-center gap-1.5">
                  <Type size={12} /> Fuentes
                </span>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {(['heading', 'body'] as const).map(key => (
                    <div key={key}>
                      <label className="text-[10px] text-white/40 capitalize block mb-1">{key === 'heading' ? 'Títulos' : 'Cuerpo'}</label>
                      <input
                        type="text"
                        value={overrideFonts[key]}
                        onChange={e => setOverrideFonts(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={activeConfig.template.theme.fonts[key] || 'Google Font name'}
                        className="w-full px-2.5 py-1.5 text-[11px] rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:ring-1 focus:ring-[var(--acc1)] outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Save / Reset */}
              <div className="flex items-center gap-3 pt-1">
                <button onClick={handleSaveOverrides} disabled={savingOverrides}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
                  style={{ background: 'var(--acc1)', color: 'var(--bg-dark)' }}>
                  {savingOverrides ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
                  Guardar overrides
                </button>
                <button onClick={handleResetOverrides} disabled={savingOverrides}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white/30 hover:text-white/50 hover:bg-white/5 transition-colors">
                  <RotateCcw size={12} /> Resetear
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Template grid */}
      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-white/30 text-sm">
          <Loader size={16} className="animate-spin" />Cargando templates…
        </div>
      ) : templates.length === 0 ? (
        <EmptyState icon={<Palette size={32} />} text="No hay templates disponibles para tu categoría" />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(tpl => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              isActive={tpl.id === activeTemplateId}
              onPreview={() => setSelected(tpl)}
              onAssign={() => handleAssign(tpl)}
              assigning={assigning}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TEMPLATE CARD
   ═══════════════════════════════════════════════════════════════ */

function TemplateCard({ template: tpl, isActive, onPreview, onAssign, assigning }: {
  template: SiteTemplate;
  isActive: boolean;
  onPreview: () => void;
  onAssign: () => void;
  assigning: boolean;
}) {
  return (
    <div className={`o-card overflow-hidden group cursor-pointer transition-all hover:border-white/20 ${
      isActive ? 'ring-1 ring-emerald-500/40 border-emerald-500/20' : ''
    }`}>
      {/* Preview image */}
      <div className="relative aspect-[16/10] bg-white/[.03] rounded-lg overflow-hidden mb-3"
           onClick={onPreview}>
        {tpl.preview_image ? (
          <img src={tpl.preview_image} alt={tpl.name}
            className="w-full h-full object-cover transition-transform group-hover:scale-105" />
        ) : (
          <ThemePreviewMini theme={tpl.theme} />
        )}
        {isActive && (
          <div className="absolute top-2 right-2">
            <Badge variant="success">
              <Check size={10} className="mr-1" />Activo
            </Badge>
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 backdrop-blur-sm text-white text-xs font-medium">
            <Eye size={12} />Ver detalles
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">{tpl.name}</h3>
          <p className="text-[11px] text-white/30 mt-0.5 line-clamp-2">{tpl.description}</p>
        </div>
      </div>

      {/* Color swatches */}
      <div className="flex items-center gap-1.5 mt-2.5">
        {Object.entries(tpl.theme.colors).slice(0, 5).map(([key, color]) => (
          <div key={key} className="w-5 h-5 rounded-full border border-white/10"
               style={{ backgroundColor: color }} title={key} />
        ))}
        <span className="text-[10px] text-white/15 ml-auto">{tpl.theme.fonts.heading}</span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[.06]">
        <button onClick={onPreview}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors">
          <Eye size={12} />Detalles
        </button>
        {!isActive && (
          <button onClick={onAssign} disabled={assigning}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all disabled:opacity-40"
            style={{ background: 'var(--acc1)', color: 'var(--bg-dark)' }}>
            {assigning ? <Loader size={10} className="animate-spin" /> : <Check size={12} />}
            Usar este
          </button>
        )}
        {isActive && (
          <span className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-emerald-400/60">
            <Check size={12} />Template activo
          </span>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MINI THEME PREVIEW (fallback when no preview_image)
   ═══════════════════════════════════════════════════════════════ */

function ThemePreviewMini({ theme }: { theme: SiteTemplateTheme }) {
  const c = theme.colors;
  return (
    <div className="w-full h-full flex flex-col" style={{ background: c.background || '#111' }}>
      {/* Fake navbar */}
      <div className="h-6 flex items-center px-3 gap-2" style={{ background: c.surface || '#222' }}>
        <div className="w-8 h-2 rounded-full" style={{ background: c.primary || '#0ff' }} />
        <div className="flex gap-1.5 ml-auto">
          {[1, 2, 3].map(i => <div key={i} className="w-4 h-1.5 rounded-full bg-white/10" />)}
        </div>
      </div>
      {/* Fake hero */}
      <div className="flex-1 flex items-center justify-center p-3">
        <div className="text-center space-y-1.5">
          <div className="w-20 h-2.5 mx-auto rounded-full" style={{ background: c.text || '#fff' }} />
          <div className="w-14 h-1.5 mx-auto rounded-full" style={{ background: `${c.text || '#fff'}40` }} />
          <div className="w-10 h-3 mx-auto mt-2 rounded" style={{ background: c.primary || '#0ff' }} />
        </div>
      </div>
      {/* Fake cards */}
      <div className="flex gap-1.5 px-3 pb-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex-1 rounded" style={{ background: c.surface || '#222', height: '24px' }} />
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TEMPLATE DETAIL (full-page preview)
   ═══════════════════════════════════════════════════════════════ */

function TemplateDetail({ template: tpl, isActive, assigning, onAssign, onBack }: {
  template: SiteTemplate;
  isActive: boolean;
  assigning: boolean;
  onAssign: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-5 max-w-4xl mx-auto">
      {/* Back */}
      <button onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-white/30 hover:text-white/50 transition-colors">
        <ChevronLeft size={14} />Volver a templates
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            {tpl.name}
            {isActive && <Badge variant="success"><Check size={10} className="mr-1" />Activo</Badge>}
          </h2>
          <p className="text-xs text-white/30 mt-1">{tpl.description}</p>
          <p className="text-[10px] text-white/15 mt-0.5">Categoría: {tpl.category}</p>
        </div>
        {!isActive && (
          <button onClick={onAssign} disabled={assigning}
            className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-40 shrink-0"
            style={{ background: 'var(--acc1)', color: 'var(--bg-dark)' }}>
            {assigning ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
            Aplicar template
          </button>
        )}
      </div>

      {/* Large preview */}
      <div className="o-card overflow-hidden">
        <div className="aspect-video rounded-lg overflow-hidden">
          {tpl.preview_image ? (
            <img src={tpl.preview_image} alt={tpl.name} className="w-full h-full object-cover" />
          ) : (
            <ThemePreviewMini theme={tpl.theme} />
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Theme colors */}
        <div className="o-card space-y-3">
          <div className="flex items-center gap-2">
            <Palette size={14} style={{ color: 'var(--acc2)' }} />
            <span className="text-sm font-semibold text-white">Colores</span>
          </div>
          <div className="space-y-2">
            {Object.entries(tpl.theme.colors).map(([key, color]) => (
              <div key={key} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg border border-white/10 shrink-0"
                     style={{ backgroundColor: color }} />
                <span className="text-[11px] text-white/50 capitalize flex-1">{key}</span>
                <span className="text-[10px] text-white/20 font-mono">{color}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Components & config */}
        <div className="space-y-4">
          {/* Fonts */}
          <div className="o-card space-y-2">
            <div className="flex items-center gap-2">
              <Type size={14} style={{ color: 'var(--acc1)' }} />
              <span className="text-sm font-semibold text-white">Tipografía</span>
            </div>
            <div className="text-xs space-y-1">
              <div className="flex justify-between">
                <span className="text-white/30">Títulos</span>
                <span className="text-white/50 font-medium">{tpl.theme.fonts.heading}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/30">Cuerpo</span>
                <span className="text-white/50 font-medium">{tpl.theme.fonts.body}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/30">Border radius</span>
                <span className="text-white/50 font-mono">{tpl.theme.borderRadius}</span>
              </div>
            </div>
          </div>

          {/* Component variants */}
          <div className="o-card space-y-2">
            <div className="flex items-center gap-2">
              <LayoutIcon size={14} style={{ color: 'var(--acc3)' }} />
              <span className="text-sm font-semibold text-white">Variantes</span>
            </div>
            <div className="text-xs space-y-1">
              {Object.entries(tpl.components).map(([comp, variant]) => (
                <div key={comp} className="flex justify-between">
                  <span className="text-white/30">{COMPONENT_LABELS[comp] || comp}</span>
                  <Badge variant="default">{VARIANT_LABELS[variant] || variant}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sections order */}
        {tpl.sections_order && tpl.sections_order.length > 0 && (
          <div className="o-card space-y-2 sm:col-span-2">
            <span className="text-[11px] uppercase tracking-widest text-white/30 font-semibold">Orden de secciones</span>
            <div className="flex flex-wrap gap-1.5">
              {tpl.sections_order.map((s, i) => (
                <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-white/5 text-white/40 border border-white/5">
                  <span className="text-white/20">{i + 1}.</span>
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplatePicker;
